import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  BookOpen,
  CheckCircle,
  ChevronRight,
  HelpCircle,
  Home,
  Key,
  ListChecks,
  Menu,
  Play,
  Terminal,
  X,
} from 'lucide-react';
import axios from 'axios';

type DocBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'steps'; items: string[] }
  | { type: 'code'; language?: string; code: string }
  | { type: 'image'; src: string; alt: string; caption?: string }
  | { type: 'callout'; tone?: 'info' | 'warning'; title: string; text: string };

interface DocSection {
  id: string;
  title: string;
  icon?: string;
  summary?: string;
  blocks?: DocBlock[];
  content?: string;
}

interface DocsPayload {
  title?: string;
  description?: string;
  lastUpdated?: string;
  version?: string;
  sections?: DocSection[];
}

const fallbackSections: DocSection[] = [
  {
    id: 'getting-started',
    title: 'Get Started',
    summary: 'Sign in, add an SSH key, launch a lab, and request a grade.',
    blocks: [
      {
        type: 'paragraph',
        text: 'Do-Labs provides temporary Linux servers for hands-on practice. Use the app to launch a lab, connect over SSH, complete the tasks, and review automated grading feedback.',
      },
    ],
  },
];

function getSectionIcon(section: DocSection) {
  switch (section.icon || section.id) {
    case 'key':
    case 'ssh-keys':
      return <Key className="h-5 w-5" />;
    case 'play':
    case 'start-lab':
      return <Play className="h-5 w-5" />;
    case 'terminal':
    case 'work-lab':
      return <Terminal className="h-5 w-5" />;
    case 'check-circle':
    case 'grades-history':
      return <CheckCircle className="h-5 w-5" />;
    case 'alert-circle':
    case 'troubleshooting':
      return <AlertCircle className="h-5 w-5" />;
    default:
      return <BookOpen className="h-5 w-5" />;
  }
}

function formatDate(value?: string) {
  if (!value) {
    return 'Current';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function renderInlineText(text: string): React.ReactNode[] {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={`${part}-${index}`} className="rounded bg-gray-800 px-1.5 py-0.5 font-mono text-sm text-blue-300">
          {part.slice(1, -1)}
        </code>
      );
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${part}-${index}`} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return part;
  });
}

function legacyBlocksFromContent(content?: string): DocBlock[] {
  if (!content) {
    return [];
  }

  return content
    .split(/\n{2,}/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => ({ type: 'paragraph', text: part } as DocBlock));
}

function renderBlock(block: DocBlock, index: number) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p key={index} className="text-gray-300 leading-relaxed">
          {renderInlineText(block.text)}
        </p>
      );
    case 'list':
      return (
        <ul key={index} className="space-y-2 text-gray-300">
          {block.items.map(item => (
            <li key={item} className="flex gap-3 leading-relaxed">
              <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-blue-400" />
              <span>{renderInlineText(item)}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol key={index} className="space-y-3 text-gray-300">
          {block.items.map((item, itemIndex) => (
            <li key={item} className="flex gap-3 leading-relaxed">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                {itemIndex + 1}
              </span>
              <span>{renderInlineText(item)}</span>
            </li>
          ))}
        </ol>
      );
    case 'code':
      return (
        <div key={index} className="overflow-hidden rounded-md border border-gray-700 bg-gray-950">
          {block.language && (
            <div className="border-b border-gray-800 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {block.language}
            </div>
          )}
          <pre className="overflow-x-auto p-4 text-sm text-green-300">
            <code>{block.code}</code>
          </pre>
        </div>
      );
    case 'image':
      return (
        <figure key={block.src} className="overflow-hidden rounded-lg border border-gray-800 bg-gray-950">
          <img
            src={block.src}
            alt={block.alt}
            loading="lazy"
            className="w-full border-b border-gray-800 bg-gray-900 object-cover"
          />
          {block.caption && (
            <figcaption className="px-4 py-3 text-sm text-gray-400">{block.caption}</figcaption>
          )}
        </figure>
      );
    case 'callout':
      return (
        <div
          key={`${block.title}-${index}`}
          className={`rounded-md border p-4 ${
            block.tone === 'warning'
              ? 'border-yellow-600/50 bg-yellow-950/30 text-yellow-100'
              : 'border-blue-700/50 bg-blue-950/30 text-blue-100'
          }`}
        >
          <div className="mb-1 font-semibold">{block.title}</div>
          <p className="text-sm leading-relaxed">{renderInlineText(block.text)}</p>
        </div>
      );
    default:
      return null;
  }
}

const UserDocs: React.FC = () => {
  const [sections, setSections] = useState<DocSection[]>(fallbackSections);
  const [title, setTitle] = useState('Do-Labs Learner Guide');
  const [description, setDescription] = useState('Concise setup, lab workflow, grading, and troubleshooting guidance.');
  const [lastUpdated, setLastUpdated] = useState<string | undefined>();
  const [version, setVersion] = useState<string | undefined>();
  const [activeSection, setActiveSection] = useState<string>('getting-started');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchDocumentation() {
      try {
        const response = await axios.get<DocsPayload>('/api/v1/docs/tutorials');
        if (!isMounted) {
          return;
        }

        const payload = response.data;
        const nextSections = Array.isArray(payload.sections) && payload.sections.length > 0
          ? payload.sections
          : fallbackSections;

        setSections(nextSections);
        setTitle(payload.title || 'Do-Labs Learner Guide');
        setDescription(payload.description || 'Concise setup, lab workflow, grading, and troubleshooting guidance.');
        setLastUpdated(payload.lastUpdated);
        setVersion(payload.version);
        setActiveSection(nextSections[0]?.id || 'getting-started');
      } catch {
        if (isMounted) {
          setSections(fallbackSections);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchDocumentation();

    return () => {
      isMounted = false;
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-300">Loading documentation...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="sticky top-0 z-50 border-b border-gray-800 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-2xl font-bold text-blue-500 hover:text-blue-400">
                Do-Labs
              </Link>
              <span className="text-gray-600">/</span>
              <span className="font-medium text-gray-200">Docs</span>
            </div>
            <div className="hidden items-center gap-4 md:flex">
              <Link to="/labs" className="text-gray-300 hover:text-white">
                Browse Labs
              </Link>
              <Link to="/help" className="text-gray-300 hover:text-white">
                Help
              </Link>
              <Link to="/login" className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
                Sign In
              </Link>
            </div>
            <button
              type="button"
              className="text-gray-300 hover:text-white md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle documentation menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="border-b border-gray-800 bg-gray-900 md:hidden">
          <nav className="container mx-auto space-y-1 px-4 py-4">
            {sections.map(section => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left ${
                  activeSection === section.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                {getSectionIcon(section)}
                <span>{section.title}</span>
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          <aside className="hidden w-64 flex-shrink-0 md:block">
            <div className="sticky top-24">
              <h2 className="mb-4 flex items-center text-lg font-semibold">
                <BookOpen className="mr-2 h-5 w-5" />
                Contents
              </h2>
              <nav className="space-y-1">
                {sections.map(section => (
                  <button
                    type="button"
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    {getSectionIcon(section)}
                    <span className="text-sm">{section.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-8 border-t border-gray-800 pt-6">
                <h3 className="mb-3 text-sm font-semibold text-gray-400">Quick Links</h3>
                <div className="space-y-2">
                  <Link to="/dashboard" className="flex items-center text-sm text-gray-400 hover:text-white">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link to="/labs" className="flex items-center text-sm text-gray-400 hover:text-white">
                    <ListChecks className="mr-2 h-4 w-4" />
                    Labs
                  </Link>
                  <Link to="/help" className="flex items-center text-sm text-gray-400 hover:text-white">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Lab Help
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          <main className="min-w-0 flex-1">
            <header className="mb-8 max-w-4xl">
              <h1 className="mb-3 text-3xl font-bold sm:text-4xl">{title}</h1>
              <p className="text-lg leading-relaxed text-gray-300">{description}</p>
            </header>

            <div className="max-w-4xl space-y-8">
              {sections.map(section => {
                const blocks = section.blocks?.length ? section.blocks : legacyBlocksFromContent(section.content);

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-24 rounded-lg border border-gray-800 bg-gray-900 p-5 sm:p-6"
                  >
                    <div className="mb-5 flex gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-blue-600">
                        {getSectionIcon(section)}
                      </div>
                      <div>
                        <h2 className="text-2xl font-semibold leading-tight">{section.title}</h2>
                        {section.summary && (
                          <p className="mt-1 text-sm leading-relaxed text-gray-400">{section.summary}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-5">{blocks.map(renderBlock)}</div>
                  </section>
                );
              })}

              <footer className="flex flex-col gap-3 border-t border-gray-800 pt-6 text-sm text-gray-400 sm:flex-row sm:items-center sm:justify-between">
                <p>
                  Last updated: {formatDate(lastUpdated)}
                  {version ? ` | Version: ${version}` : ''}
                </p>
                <Link to="/labs" className="text-blue-400 hover:text-blue-300">
                  Start learning
                </Link>
              </footer>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserDocs;
